import { useCallback } from 'react';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { useSelector, useDispatch } from '@cbridge-utils/react-redux';
import { queryParamsSelector, SearchParamName, RouterAction } from '@cbridge-packages/routing';
import { UseGridSettingsArgs, UseGridSettingsResult } from './useGridSettings.interface';

const parseSortParam = (param: string): GridSortModel[number] => ({
  field: param.replace(/^-/, ''),
  sort: param[0] === '-' ? 'desc' : 'asc',
});

export const useGridSettings = (args: UseGridSettingsArgs = {}): UseGridSettingsResult => {
  const dispatch = useDispatch();
  const queryParam = useSelector(queryParamsSelector, { name: SearchParamName.Sort });
  const { columns } = args;

  // The sidebar navigation carries `?sort=` over from the previous page. Entries naming a column
  // this grid does not have, or has made non-sortable, are dropped instead of being sent to the
  // backend; with nothing left the grid falls back to its own initial sorting (LB-4553).
  const isSortableField = (field: string): boolean =>
    !columns || columns.some((column) => column.field === field && column.sortable !== false);

  const urlSorting = queryParam.map(parseSortParam).filter((item) => isSortableField(item.field));
  const sorting = urlSorting.length ? urlSorting : args.sorting;

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const sorting = model.map((item) => `${item.sort === 'desc' ? '-' : ''}${item.field}`);
    if (sorting.length) {
      dispatch(RouterAction.addParam({ name: SearchParamName.Sort, value: sorting }));
    } else {
      dispatch(RouterAction.removeParam({ name: SearchParamName.Sort }));
    }
  }, [dispatch]);

  return {
    initialState: {
      sorting: {
        sortModel: sorting,
      },
    },
    onSortModelChange,
  };
};
